import type { ReactElement } from "react";
import type { KategorieZeile } from "@kundeninfo/api-client";
import { Icon, Objektliste, Objektzeile } from "@kundeninfo/ui";
import { Knopf } from "@kundeninfo/ui/me-design";
import { ARTICLE_TEXT as T, missingAccount, priceRange } from "./articleTexts.js";

/** Der Schlüssel der Pseudozeile „ohne Kategorie“ (Vertrag `KategorieZeile.key`, Plan P4). */
export const OHNE_KATEGORIE = "none";

/** Der Name einer Kategorienzeile: „ohne Kategorie“ aus der Texttabelle — der Server liefert die Zeile mit leerem
 *  Namen, er wird nicht gezeigt. */
export const kategorieName = (z: KategorieZeile): string => (z.key === OHNE_KATEGORIE ? T.withoutCategory : z.name);

/** „ohne Kategorie“ ohne Treffer — der Server lässt die Zeile dann ganz weg (`HAVING COUNT(*) > 0`). */
const OHNE_KATEGORIE_LEER: KategorieZeile = { key: OHNE_KATEGORIE, name: "", productCategoryIds: [], companyIds: [],
  count: 0, minNetUnitPrice: null, maxNetUnitPrice: null, missingAccountCount: 0 };

/**
 * Die Zeilen, die die Oberfläche zeigt: in Serverfolge, „ohne Kategorie“ nur mit Artikeln (778) — außer sie ist
 * GEWÄHLT (I-1): Dann bleibt sie stehen, notfalls mit 0, statt dass eine Suche ohne Treffer oder die ausgeschaltete
 * Pille sie still auf „Alle“ zurückwirft; das Raster sagt dann „keine Treffer“ bzw. „keine Artikel“.
 */
export function sichtbareZeilen(zeilen: readonly KategorieZeile[], gewaehlt: string | null = null): KategorieZeile[] {
  const halten = gewaehlt === OHNE_KATEGORIE;
  const sicht = zeilen.filter((z) => z.key !== OHNE_KATEGORIE || z.count > 0 || halten);
  return halten && !sicht.some((z) => z.key === OHNE_KATEGORIE) ? [...sicht, OHNE_KATEGORIE_LEER] : sicht;
}

/** Die Zusammenfassung „Alle Artikel“: Zähler, Preisspanne und Warnzähler über alle Zeilen. */
export type KategorieSumme = { count: number; min: number | null; max: number | null; missingAccountCount: number };

export function summe(zeilen: readonly KategorieZeile[]): KategorieSumme {
  const mins = zeilen.flatMap((z) => (z.minNetUnitPrice === null ? [] : [z.minNetUnitPrice]));
  const maxs = zeilen.flatMap((z) => (z.maxNetUnitPrice === null ? [] : [z.maxNetUnitPrice]));
  return {
    count: zeilen.reduce((n, z) => n + z.count, 0),
    min: mins.length === 0 ? null : Math.min(...mins), max: maxs.length === 0 ? null : Math.max(...maxs),
    missingAccountCount: zeilen.reduce((n, z) => n + z.missingAccountCount, 0),
  };
}

export type KategorienListeProps = {
  /** `null` = der erste Katalog ist noch nicht da. */
  zeilen: readonly KategorieZeile[] | null;
  /** Der Ladefehler (auch 413) — in der Liste getrennt vom Raster gemeldet. */
  fehler: string | null;
  /** Der Schlüssel der gewählten Zeile, `null` = „Alle Artikel“. */
  gewaehlt: string | null;
  istAdmin: boolean;
  onWahl: (key: string | null) => void;
  onPflege: () => void;
};

/**
 * Die KATEGORIENLISTE links (Spalte `MeSchale.list`): Kopf „Kategorien n“ mit dem Stift der Pflege (nur ADMIN, 776),
 * darunter „Alle Artikel“ und die Zeilen in Serverfolge (796, 777). Jede Zeile trägt Zähler, Preisspanne und — nur bei
 * Artikeln ohne Konto — den Status „n ohne Konto“ (`warnung`). Die Zähler hängen an Unternehmen, Pille und Suche, nicht
 * an der gewählten Kategorie; ein Klick filtert die geladene Menge ohne neue Anfrage (792).
 */
export function KategorienListe(p: KategorienListeProps): ReactElement {
  const zeilen = p.zeilen === null ? null : sichtbareZeilen(p.zeilen, p.gewaehlt);
  const anzahl = zeilen?.filter((z) => z.key !== OHNE_KATEGORIE).length;
  const zeile = (key: string | null, name: string, z: KategorieSumme): ReactElement => (
    <Objektzeile key={key ?? ""} name={name} zaehler={z.count} meta={priceRange(z.min, z.max)}
      {...(z.missingAccountCount > 0
        ? { status: { text: missingAccount(z.missingAccountCount), art: "warnung" as const } } : {})}
      gewaehlt={p.gewaehlt === key} ariaLabel={T.categoryRow(name, z.count)} onOeffnen={() => p.onWahl(key)} />
  );
  return (
    <div className="bart__kat">
      <div className="bart__kat-kopf">
        <h2 className="bart__kat-titel">{T.categories}</h2>
        {anzahl !== undefined && <span className="bart__kat-anzahl">{anzahl}</span>}
        {p.istAdmin && (
          <Knopf className="bart__kat-pflege" art="leise" kompakt aria-label={T.editCategories} onClick={p.onPflege}>
            <Icon name="pen" size={14} />
          </Knopf>
        )}
      </div>
      <div className="bart__kat-liste">
        {p.fehler !== null ? <p className="bart__meldung bart__meldung--fehler" role="alert">{p.fehler}</p>
          : zeilen === null ? <p className="bart__meldung" role="status">{T.loading}</p>
          : (
            <Objektliste label={T.categories}>
              {zeile(null, T.allArticles, summe(zeilen))}
              {zeilen.map((z) => zeile(z.key, kategorieName(z), {
                count: z.count, min: z.minNetUnitPrice, max: z.maxNetUnitPrice,
                missingAccountCount: z.missingAccountCount,
              }))}
            </Objektliste>
          )}
      </div>
    </div>
  );
}
